/**
 * NDJSON (Newline Delimited JSON) streaming parser utility
 *
 * Handles parsing of streaming NDJSON responses with proper brace counting,
 * escape sequence handling, and string detection for robust JSON object extraction.
 */

import { Sentry } from '@/modules/sentry';

/**
 * Extended parsing error that includes events successfully parsed before interruption.
 */
interface IStreamParsingError<T> extends Error {
  /** Successfully parsed objects emitted before the parser failed. */
  partialResults?: T[];
}

/**
 * Safely extracts partial stream results from a thrown parsing error.
 *
 * @param error - Unknown error thrown during stream parsing
 * @returns Parsed objects collected before failure, or an empty array
 */
export function getPartialStreamResults<T>(error: unknown): T[] {
  if (
    error &&
    typeof error === 'object' &&
    'partialResults' in error &&
    Array.isArray((error as IStreamParsingError<T>).partialResults)
  ) {
    return [...((error as IStreamParsingError<T>).partialResults ?? [])];
  }

  return [];
}

/**
 * Parses a streaming NDJSON response and calls the provided callback for each parsed object
 *
 * @param reader - The ReadableStreamDefaultReader from the fetch response
 * @param onChunk - Callback function called for each parsed JSON object
 * @param signal - Optional AbortSignal to cancel the parsing
 * @returns Promise that resolves to an array of all parsed objects
 */
export async function parseNDJSONStream<T>(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onChunk: (data: T) => void,
  signal?: AbortSignal,
  requestId?: string,
): Promise<T[]> {
  const decoder = new TextDecoder();
  const results: T[] = [];
  let buffer = '';

  try {
    while (true) {
      // Check if the operation was aborted
      if (signal?.aborted) {
        throw new Error('Stream parsing was aborted');
      }

      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete JSON objects in the buffer
      const { parsedObjects, remainingBuffer } =
        parseJSONObjectsFromBuffer<T>(buffer);

      // Add parsed objects to results and call callback
      for (const obj of parsedObjects) {
        results.push(obj);
        onChunk(obj);
      }

      // Update buffer with remaining content
      buffer = remainingBuffer;

      Sentry.logger.debug('NDJSON stream buffer ', {
        buffer,
        value,
        requestId,
        parsedCount: results.length,
      });
    }

    // Process any remaining data in the buffer
    if (buffer.trim()) {
      try {
        const finalObject = JSON.parse(buffer.trim()) as T;
        results.push(finalObject);
        onChunk(finalObject);
      } catch (parseError) {
        Sentry.logger.warn('Failed to parse final NDJSON object:', {
          buffer: buffer.trim(),
          parseError,
          requestId,
        });
      }
    }

    return results;
  } catch (error) {
    // Don't log expected stream terminations:
    // - Intentional abort via AbortSignal
    // - Browser-level DOMException AbortError (same thing, different surface)
    // - Chrome "TypeError: network error": fired when TCP connection drops
    //   mid-stream (WiFi loss, server restart). Not a code bug — suppress Sentry
    //   noise (BUMBLEBEE-5, x6740 events).
    const isExpectedTermination =
      signal?.aborted ||
      (error instanceof DOMException && error.name === 'AbortError') ||
      (error instanceof TypeError && error.message === 'network error');

    if (!isExpectedTermination) {
      Sentry.logger.warn('NDJSON parsing error:', { error, requestId });
    }

    const streamParsingError =
      error instanceof Error
        ? (error as IStreamParsingError<T>)
        : (new Error('NDJSON parsing failed') as IStreamParsingError<T>);

    // Cache partial parsed results so callers can gracefully degrade and avoid data loss.
    streamParsingError.partialResults = [...results];
    throw streamParsingError;
  } finally {
    // Clean up the reader
    try {
      reader.releaseLock();
    } catch (_error) {
      // Reader might already be released
    }
  }
}

/**
 * Parses complete JSON objects from a buffer, handling partial objects
 *
 * @param buffer - The current buffer content
 * @returns Object containing parsed objects and remaining buffer content
 */
function parseJSONObjectsFromBuffer<T>(buffer: string): {
  parsedObjects: T[];
  remainingBuffer: string;
} {
  const parsedObjects: T[] = [];
  let startIdx = 0;
  let braceCount = 0;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < buffer.length; i++) {
    const char = buffer[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"' && !escapeNext) {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === '{') {
        if (braceCount === 0) {
          startIdx = i; // Mark the start of a new JSON object
        }
        braceCount++;
      } else if (char === '}') {
        braceCount--;

        // We found a complete JSON object
        if (braceCount === 0) {
          const jsonStr = buffer.substring(startIdx, i + 1).trim();
          if (jsonStr) {
            try {
              const parsedObject = JSON.parse(jsonStr) as T;
              parsedObjects.push(parsedObject);
            } catch (parseError) {
              console.warn('Failed to parse JSON object:', parseError);
            }
          }
          // Move past this JSON object
          buffer = buffer.substring(i + 1);
          i = -1; // Reset counter as we've modified the buffer
        }
      }
    }
  }

  return { parsedObjects, remainingBuffer: buffer };
}
